import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search, ArrowLeft, Save, Pencil, User, Car } from "lucide-react";

const inputCls = "w-full bg-black border border-zinc-800 p-2.5 text-sm text-zinc-200 focus:border-brand-red outline-none";
const labelCls = "text-[10px] font-mono uppercase tracking-widest text-zinc-500";
const STATES = ["TAS", "VIC", "NSW", "QLD", "SA", "WA", "NT", "ACT"];

const BookingEditor = ({ appt, onSaved }) => {
  const [f, setF] = useState({
    user_name: appt.user_name || "", user_phone: appt.user_phone || "",
    vehicle_make: appt.vehicle_make || "", vehicle_model: appt.vehicle_model || "",
    vehicle_year: appt.vehicle_year || "", vehicle_rego: appt.vehicle_rego || "",
    vehicle_state: appt.vehicle_state || "TAS", preferred_date: appt.preferred_date || "",
    preferred_time: appt.preferred_time || "", notes: appt.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/appointments/${appt.id}/details`, {
        ...f,
        vehicle_year: f.vehicle_year === "" ? null : Number(f.vehicle_year),
        vehicle_rego: f.vehicle_rego || null,
        notes: f.notes || null,
      });
      toast.success("Booking updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`booking-editor-${appt.id}`} className="border-t border-zinc-900 bg-black/40 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div className="col-span-2"><span className={labelCls}>Name</span><input data-testid={`edit-name-${appt.id}`} className={inputCls} value={f.user_name} onChange={set("user_name")} /></div>
      <div className="col-span-2"><span className={labelCls}>Phone</span><input data-testid={`edit-phone-${appt.id}`} className={inputCls} value={f.user_phone} onChange={set("user_phone")} /></div>
      <div><span className={labelCls}>Make</span><input data-testid={`edit-make-${appt.id}`} className={inputCls} value={f.vehicle_make} onChange={set("vehicle_make")} /></div>
      <div><span className={labelCls}>Model</span><input data-testid={`edit-model-${appt.id}`} className={inputCls} value={f.vehicle_model} onChange={set("vehicle_model")} /></div>
      <div><span className={labelCls}>Year</span><input data-testid={`edit-year-${appt.id}`} type="number" className={inputCls} value={f.vehicle_year} onChange={set("vehicle_year")} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><span className={labelCls}>Rego</span><input data-testid={`edit-rego-${appt.id}`} className={inputCls} value={f.vehicle_rego} onChange={(e) => setF({ ...f, vehicle_rego: e.target.value.toUpperCase() })} /></div>
        <div><span className={labelCls}>State</span>
          <select className={inputCls} value={f.vehicle_state} onChange={set("vehicle_state")}>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
        </div>
      </div>
      <div><span className={labelCls}>Date</span><input data-testid={`edit-date-${appt.id}`} type="date" className={inputCls} value={f.preferred_date} onChange={set("preferred_date")} /></div>
      <div><span className={labelCls}>Time</span><input data-testid={`edit-time-${appt.id}`} type="time" className={inputCls} value={f.preferred_time} onChange={set("preferred_time")} /></div>
      <div className="col-span-2"><span className={labelCls}>Notes</span><input data-testid={`edit-notes-${appt.id}`} className={inputCls} value={f.notes} onChange={set("notes")} /></div>
      <div className="col-span-2 sm:col-span-4">
        <button data-testid={`edit-save-${appt.id}`} onClick={save} disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-red text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
          <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save booking"}
        </button>
      </div>
    </div>
  );
};

const CustomerDetail = ({ email, onBack }) => {
  const [data, setData] = useState(null);
  const [contact, setContact] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = React.useCallback((em) => {
    api.get("/admin/customers/detail", { params: { email: em } }).then((r) => {
      setData(r.data);
      setContact({ name: r.data.name || "", phone: r.data.phone || "", email: r.data.email });
    }).catch(() => toast.error("Couldn't load customer"));
  }, []);
  React.useEffect(() => { load(email); }, [email, load]);

  if (!data || !contact) return <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-8">Loading customer…</div>;

  const saveContact = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/admin/customers/detail", {
        email: data.email, name: contact.name, phone: contact.phone,
        new_email: contact.email !== data.email ? contact.email : null,
      });
      toast.success("Customer details updated everywhere");
      load(r.data.email);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="customer-detail">
      <button data-testid="customer-back" onClick={onBack} className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400 hover:text-brand-red">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to search
      </button>

      <div className="mt-4 border border-zinc-900 bg-zinc-950 p-5">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-10 w-10 grid place-items-center bg-zinc-900"><User className="h-4 w-4 text-brand-red" /></span>
          <div>
            <div className="font-display uppercase text-xl text-white tracking-tight">{data.name || data.email}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{data.has_account ? "Registered account" : "Guest (no account)"} · {data.appointments.length} booking{data.appointments.length !== 1 ? "s" : ""}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><span className={labelCls}>Name</span><input data-testid="customer-edit-name" className={inputCls} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} /></div>
          <div><span className={labelCls}>Phone</span><input data-testid="customer-edit-phone" className={inputCls} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} /></div>
          <div><span className={labelCls}>Email</span><input data-testid="customer-edit-email" type="email" className={inputCls} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></div>
        </div>
        <button data-testid="customer-save-contact" onClick={saveContact} disabled={busy}
          className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 bg-brand-red text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
          <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save contact details"}
        </button>
        <p className="mt-2 text-[11px] text-zinc-600">Updates the customer's account and all their bookings at once.</p>
      </div>

      {data.saved_vehicles.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {data.saved_vehicles.map((v) => (
            <span key={v.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 text-xs text-zinc-300">
              <Car className="h-3 w-3 text-brand-red" /> {v.year} {v.make} {v.model}{v.rego ? ` · ${v.rego}` : ""}
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 space-y-px bg-zinc-900 border border-zinc-900" data-testid="customer-detail-bookings">
        {data.appointments.map((a) => (
          <div key={a.id} className="bg-zinc-950">
            <div className="p-4 flex flex-wrap items-center gap-4">
              <div className="min-w-[120px]">
                <div className="font-mono text-xs text-zinc-300">{a.preferred_date}</div>
                <div className="font-mono text-[10px] text-zinc-500">{a.preferred_time}</div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm text-white font-semibold">{a.vehicle_year} {a.vehicle_make} {a.vehicle_model}{a.vehicle_rego ? ` · ${a.vehicle_rego}` : ""}</div>
                <div className="text-xs text-zinc-500">{a.service}{a.work_done ? ` — ${a.work_done}` : ""}{a.price_charged != null ? ` — $${Number(a.price_charged).toFixed(2)}` : ""}</div>
              </div>
              <span className="px-2.5 py-1 border border-zinc-700 text-[10px] font-mono uppercase tracking-widest text-zinc-400">{(a.status || "").replace("_", " ")}</span>
              <button data-testid={`booking-edit-toggle-${a.id}`} onClick={() => setEditing(editing === a.id ? null : a.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] font-mono uppercase tracking-widest ${editing === a.id ? "border-brand-red text-brand-red" : "border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red"}`}>
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </div>
            {editing === a.id && <BookingEditor appt={a} onSaved={() => { setEditing(null); load(data.email); }} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function CustomerSearch({ customers = [] }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) { setResults(null); return; }
    setSearching(true);
    try {
      const r = await api.get("/admin/customers/search", { params: { q: q.trim() } });
      setResults(r.data);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  if (selected) return <CustomerDetail email={selected} onBack={() => setSelected(null)} />;

  const rows = results !== null
    ? results
    : customers.map((c) => ({ email: c.email, name: c.name, phone: c.phone, has_account: true, vehicles: [], bookings_count: null, last_visit: (c.created_at || "").slice(0, 10) }));

  return (
    <div data-testid="customer-search">
      <form onSubmit={search} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
          <input
            data-testid="customer-search-input"
            className="w-full bg-black border border-zinc-800 pl-10 pr-3 py-3 text-sm text-zinc-200 focus:border-brand-red outline-none"
            placeholder="Search by name, phone or rego…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button data-testid="customer-search-btn" disabled={searching}
          className="px-6 py-3 bg-brand-red text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
          {searching ? "…" : "Search"}
        </button>
      </form>
      {results !== null && (
        <p className="mb-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{results.length} match{results.length !== 1 ? "es" : ""} — includes guests without accounts</p>
      )}
      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 border-b border-zinc-900">
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Phone</th>
              <th className="p-4">Vehicles</th><th className="p-4">Last visit</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.email} data-testid={`customer-row-${c.email}`} onClick={() => setSelected(c.email)}
                className="border-b border-zinc-900 bg-zinc-950 cursor-pointer hover:bg-zinc-900/60">
                <td className="p-4 text-white">{c.name || "—"}</td>
                <td className="p-4 text-zinc-300">{c.email}</td>
                <td className="p-4 text-zinc-300">{c.phone || "—"}</td>
                <td className="p-4 text-zinc-500 text-xs">{(c.vehicles || []).slice(0, 2).join(", ") || "—"}</td>
                <td className="p-4 text-zinc-500 font-mono text-xs">{c.last_visit || "—"}</td>
                <td className="p-4 text-brand-red text-xs font-mono uppercase tracking-widest">View / edit →</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td className="p-6 text-zinc-500" colSpan={6}>No customers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
